/**
 * Level-of-detail gates for the Knowledge Archive graph.
 *
 * Returns the two predicates `react-force-graph` needs — which nodes to paint
 * and which links to draw — given the camera. The arithmetic lives in
 * `lib/mindspace/graphLod`; this hook is the React wiring and the memoisation.
 *
 * Why predicates and not a filtered node array: a new `graphData` identity
 * makes the library re-index d3, re-register hit-test colours, re-heat the
 * simulation and possibly snap the camera — during the very gesture that
 * caused the change. `nodeVisibility` / `linkVisibility` are registered
 * `triggerUpdate: false` and cost a single redraw.
 */

import { useCallback, useMemo } from 'react';
import {
  clusterBudget,
  clusterIsLegible,
  isInRect,
  visibleRect,
} from '@/lib/mindspace/graphLod';

/**
 * Groups drawn however far out the camera is, biggest first.
 *
 * Without it a small archive zoomed out past its fit scale shows NOTHING: the
 * legibility gate is a pixel threshold, and twelve groups of ten concepts all
 * fall under it below ~30%. A floor on the number of groups is also a constant
 * set, so unioning it with the gate keeps the whole thing monotonic in zoom.
 */
const ALWAYS_DRAWN_CLUSTERS = 8;

export interface LodNode {
  id: string;
  x?: number;
  y?: number;
  rank?: number;
  /** Which cluster it belongs to — picks that cluster's budget. */
  subject?: string;
}

export interface LodCluster {
  subject: string;
  x: number;
  y: number;
  /** Graph units. Decides whether the cluster is drawn at all, by screen size. */
  r: number;
  /** Concepts in it — orders the "always drawn" floor. */
  count: number;
}

export interface LodLink {
  source: string | { id?: string } | undefined;
  target: string | { id?: string } | undefined;
  kind?: 'co_occurrence' | 'similarity' | 'prerequisite' | null;
}

export interface UseGraphLodOptions {
  nodes: readonly LodNode[];
  clusters: readonly LodCluster[];
  camera: { k: number; cx: number; cy: number };
  dimensions: { width: number; height: number };
  /** The ring test seam draws everything; LOD is product behaviour only. */
  disabled: boolean;
  /**
   * The concept being asked about — hovered, or clicked and still open. One
   * value rather than the two this used to take, because the painter decides
   * what focus means (hover previews, a click pins) and the predicate must not
   * get a second opinion about it.
   */
  focusId: string | null;
  /**
   * Everything the focused concept links to. Neither these nodes nor their
   * links to it are ever culled — see `nodeIsVisible`.
   */
  focusNeighbours: ReadonlySet<string>;
  /** Reads an endpoint id; the library swaps strings for node objects. */
  idOf: (end: LodLink['source']) => string;
}

export function useGraphLod({
  nodes,
  clusters,
  camera,
  dimensions,
  disabled,
  focusId,
  focusNeighbours,
  idOf,
}: UseGraphLodOptions) {
  /**
   * Which clusters are drawn: big enough on screen, or one of the biggest
   * regardless. Both halves are monotonic in zoom, so the set only grows as
   * you zoom in — see `clusterBudget` for why that is non-negotiable.
   *
   * Computed once per camera change rather than per node per frame.
   */
  const drawnSubjects = useMemo(() => {
    const biggest = [...clusters]
      .sort((a, b) => b.count - a.count)
      .slice(0, ALWAYS_DRAWN_CLUSTERS)
      .map(c => c.subject);
    const drawn = new Set(biggest);
    for (const c of clusters) {
      if (clusterIsLegible(c.r, camera.k)) drawn.add(c.subject);
    }
    return drawn;
  }, [clusters, camera.k]);

  /** How deep into each drawn cluster we go. Zoom alone decides. */
  const nodeBudget = clusterBudget(camera.k);

  const nodeIsVisible = useCallback(
    (node: LodNode) => {
      if (disabled) return true;
      // The focused concept and its neighbourhood are never culled, by rank,
      // by cluster or by viewport. Asking "what is this connected to" and
      // then having half the answer thin out as you zoom is the opposite of
      // an answer; off-screen neighbours simply draw their line out of frame.
      if (node.id === focusId || focusNeighbours.has(node.id)) return true;
      // A node in a cluster too small to resolve is not drawn either: the
      // group is the unit of legibility, and loose nodes from a group whose
      // name is not shown are unattributable dots.
      if (!drawnSubjects.has(node.subject ?? '')) return false;
      if ((node.rank ?? 0) >= nodeBudget) return false;
      return isInRect(
        node.x,
        node.y,
        visibleRect({ ...camera, ...dimensions })
      );
    },
    [
      disabled,
      focusId,
      focusNeighbours,
      drawnSubjects,
      nodeBudget,
      camera,
      dimensions,
    ]
  );

  /**
   * Built once per camera change rather than re-derived per link:
   * `linkVisibility` is called for every link on every painted frame.
   */
  const visibleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const node of nodes) if (nodeIsVisible(node)) ids.add(node.id);
    return ids;
  }, [nodes, nodeIsVisible]);

  /**
   * Which lines are drawn. One rule at rest, one exception on demand.
   *
   * - **At rest** — only edges INSIDE a cluster, and only when both ends
   *   survived the node gates. Zoom into a group and you see how that group
   *   hangs together; nothing is drawn between groups you are not asking
   *   about.
   * - **Focused** — a hovered or clicked concept shows every link it has, to
   *   any cluster, at any length and any zoom. That is the question being
   *   asked by pointing at it, and it is the only place the restriction lifts.
   *
   * There is no zoom rung here any more, and no length cap. Both were trying
   * to answer "is this edge worth the ink" with arithmetic, when cluster
   * membership answers it directly: an intra-cluster edge can only be drawn
   * once its cluster is legible and on screen, which IS the zoom gate, applied
   * one level up in `nodeIsVisible`. It also means the rule cannot un-draw a
   * link as you zoom in, the way a pixel-length cap would.
   *
   * Measured on a live archive, this is what the restriction costs and buys:
   * of 1,993 drawable links, 1,505 are cross-cluster with a median length of
   * ~800 graph units — most of the width of the layout. Drawn at rest they
   * were chords across the viewport, each one spending the legibility of
   * everything it crossed to say "these two are related".
   *
   * `co_occurrence` is NEVER drawn, not even on focus. The backend writes one
   * between every pair of concepts in a lesson (435 for a 30-concept lesson,
   * all at `weight=1.0`), and that single population is what made the old
   * graph a hairball. Position already carries it: those concepts sit in the
   * same cluster. On focus it would fan out twenty-nine lines from one node.
   */
  const linkIsVisible = useCallback(
    (link: LodLink) => {
      if (disabled) return true;
      if (link.kind === 'co_occurrence') return false;

      const sourceId = idOf(link.source);
      const targetId = idOf(link.target);

      /**
       * You asked, so nothing is rationed: every link of the focused concept
       * is drawn, at any zoom, to any cluster, however long, and whether or
       * not the far end is on screen.
       *
       * This test runs BEFORE the visibility gate below, and the order is the
       * whole point. With it after, a selected concept's neighbourhood came
       * apart as the camera moved — zoom in and the links to anything now
       * off-screen vanished, zoom out and the ones whose far end fell below
       * the rank gate went with them. The set of edges a concept has does not
       * depend on where you happen to be looking.
       */
      if (sourceId === focusId || targetId === focusId) return true;

      // At rest no edges at all. The lesson glows carry grouping and each
      // lesson's constellation carries its structure; a link is drawn only
      // when someone points at a concept and asks what it connects to.
      return false;
    },
    [disabled, focusId, idOf]
  );

  return { nodeIsVisible, linkIsVisible, visibleIds, drawnSubjects };
}
